'use client';

/**
 * @fileoverview Shared Discover envelope totals hook (WEB-786).
 *
 * Reads `meta.total` + `meta.tasteMatchedTotal` from the SAME
 * `useDiscoverEventsQuery(buildDiscoverQueryArgs(...))` cache entry the
 * results bar / list / chrome already subscribe to. No second
 * `/events/discover` request — the cold-load invariant stays intact.
 */

import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverEventsQuery } from '@loci/api';

import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';
import {
  resolveDiscoverEnvelopeTotals,
  serializeDiscoverTotalsScope,
  type DiscoverEnvelopeTotals,
  type DiscoverEnvelopeTotalsFallback,
} from '@/lib/discover/envelopeTotals';

/**
 * Resolve corpus-wide in-range + taste-matched totals for the active
 * city + filter set. Optional SSR fallbacks cover the anonymous seed
 * until the live subscription lands (authed viewers miss that seed on
 * purpose — see `loadDiscoverSurfaceData`).
 *
 * The fallback is bound to the query arguments it was measured for. The SSR
 * payload describes the filter set the page was rendered with; a category /
 * neighborhood / date change re-keys the subscription, and until the new
 * envelope lands there is no measured total for that filter set. Reusing the
 * fallback there would mark the OLD number resolved and paint it under the
 * NEW filters — the same class of disagreeing count this module exists to
 * prevent. Once the args differ the fallback is dropped and every consumer
 * skeletons until the matching envelope arrives.
 *
 * The live envelope is read from `currentData`, NOT `data`. RTK Query keeps
 * the last successful response in `data` across an argument change, so after
 * a filter change `data` still holds the previous filter set's envelope —
 * the identical stale-count defect, arriving through the cache instead of
 * through the SSR fallback. `currentData` is argument-matched and goes
 * `undefined` while the new request is in flight, which is exactly the
 * pending state every consumer must skeleton on.
 */
export function useDiscoverEnvelopeTotals(
  cityCode: CityCode,
  fallback?: DiscoverEnvelopeTotalsFallback,
): DiscoverEnvelopeTotals {
  const { params } = useDiscoverParams();
  const queryArgs = useDiscoverEventArgs(params, cityCode);
  const { currentData } = useDiscoverEventsQuery(queryArgs);

  // Compared as a stable string, not by reference: `params` (and therefore
  // `queryArgs`) is rebuilt whenever `useSearchParams` hands back a new
  // object, so an unchanged URL must still compare equal to the mount scope.
  const scopeKey = React.useMemo(() => serializeDiscoverTotalsScope(queryArgs), [queryArgs]);
  const initialScopeKeyRef = React.useRef(scopeKey);
  const fallbackApplies = scopeKey === initialScopeKeyRef.current;

  const inRangeFallback = fallbackApplies ? fallback?.inRangeTotal : undefined;
  const tasteFallback = fallbackApplies ? fallback?.tasteMatchedTotal : undefined;
  const neighborhoodFallback = fallbackApplies ? fallback?.neighborhoodCount : undefined;

  return React.useMemo(
    () =>
      resolveDiscoverEnvelopeTotals(currentData, {
        inRangeTotal: inRangeFallback,
        tasteMatchedTotal: tasteFallback,
        neighborhoodCount: neighborhoodFallback,
      }),
    [currentData, inRangeFallback, tasteFallback, neighborhoodFallback],
  );
}
